import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import {
  Container,
  FontAwesomeButton,
  Page,
  PageTitle,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  useOrganization,
  useTeam,
} from '@foxtrotplatform/platform-micro-site-core';
import { Button } from '@windmill/react-ui';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { readApplicationForSubmission } from '../../common/api/application';
import { DeveloperOrganizationContextProps } from '../../common/providers/types';

export const ApplicationPublishResult: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const [submissionResult, setSubmissionResult] = useState<string>();
  const useQuery = () => {
    const { search } = useLocation();

    return React.useMemo(() => new URLSearchParams(search), [search]);
  };
  const [statusParam] = useQuery();

  useEffect(() => {
    if (!statusParam) {
      return;
    }

    if (statusParam.length > 0) {
      setSubmissionResult(statusParam[1]);
    }
  }, [statusParam]);

  const supportEmail = 'support@OS1.delhivery.com';
  const { applicationId } = useParams();
  const { currentOrg, currentTenant } =
    useOrganization() as DeveloperOrganizationContextProps;
  const { currentTeam } = useTeam();
  const [application, setApplication] = React.useState<ApplicationSubmission>();

  React.useEffect(() => {
    if (
      applicationId &&
      currentOrg &&
      currentTenant &&
      currentTeam &&
      !application
    ) {
      const getApplication = async () => {
        const app = await readApplicationForSubmission(
          currentOrg.id,
          currentTeam.id,
          applicationId
        );

        if (app) {
          setApplication(app);
        }
      };
      getApplication();
    }
  }, [applicationId, currentOrg, currentTeam, currentTenant]);

  return (
    <Page className="flex-col items-center">
      <Container
        className="mb-os1-spacing flex flex-col justify-start gap-6"
        size="medium"
      >
        <div className="flex flex-row items-center gap-x-3 pb-os1-spacing ">
          <div className="border border-gary-100 w-7 h-7 pt-px pl-1.5 rounded-md">
            <FontAwesomeButton
              icon={faArrowLeft}
              onClick={() => navigate('/application/check')}
              test_id="back-button"
            />
          </div>
          <PageTitle>
            App Submission {createSubmissionResultText(submissionResult)}
          </PageTitle>
        </div>
        {application && (
          <strong>
            App Version ID: {getAppVersionHash(application?.versionId)}
          </strong>
        )}
        {submissionResult?.toLowerCase() === 'success' && (
          <div>
            <p>
              Thank you for submitting {getAppName(application?.listingName)}{' '}
              app. Our platform operations team will review your submission.
              They will be in touch with you shortly. Please contact us at{' '}
              <span>{getSupportEmailLink(supportEmail)}</span> with any
              questions or concerns and use your App Version ID number in the
              subject line.
            </p>
            <Button
              className="mt-8 mb-4 mr-4"
              onClick={() => navigate('/application/check')}
            >
              View Apps
            </Button>
          </div>
        )}
        {submissionResult?.toLowerCase() !== 'success' && (
          <div>
            <p>
              We're sorry, the app submission process ran into an error. Please
              contact our support team at
              <span>{getSupportEmailLink(supportEmail)}</span>.
            </p>
          </div>
        )}
      </Container>
    </Page>
  );
};

const createSubmissionResultText = (result: string | undefined): string => {
  if (!result) {
    return 'Submitting...';
  }

  if (result.toLowerCase() === 'success') {
    return 'Successful';
  } else {
    return 'Failed';
  }
};

const getAppVersionHash = (appVersionFull?: string): string => {
  if (!appVersionFull) {
    return '';
  }

  const parts: string[] = appVersionFull.split('appversion:');

  if (parts.length > 1) {
    return parts[1];
  } else {
    return '';
  }
};

const getAppName = (appName?: string): string => {
  if (!appName) {
    return 'your';
  }

  return `"${appName}"`;
};

const getSupportEmailLink = (email: string) => {
  return (
    <a className="underline text-blue-600" href={`mailto:${email}`}>
      {email}
    </a>
  );
};
